import CoreLoader from '@components/CoreLoader'
import cn from 'classnames'
import type { PaymentOptionResponse } from 'mmfintech-backend-api/types'
import { isValidArray, useMatchMedia } from 'mmfintech-commons'
import { useState } from 'react'
import { DepositMethodInfoCard } from '../DepositMethodInfoCard'
import { isMethodDisabled, type PaymentMethodOption } from '../PaymentMethodSelect'
import Collapsible from './Collapsible'
import styles from './DepositPaymentMethodSelect.module.scss'
import MethodCard from './MethodCard'

const COLLAPSE_BREAKPOINT_PX = 1280

type DepositPaymentMethodSelectProps = {
  paymentOptions: PaymentMethodOption[]
  selectedPaymentOption?: PaymentOptionResponse
  setSelectedPaymentOption?: (value: string, path?: string) => void
  currencyCode?: string
  loading?: boolean
  disabled?: boolean
  ignoreStatus?: boolean
}

export const DepositPaymentMethodSelect = (props: DepositPaymentMethodSelectProps) => {
  const {
    paymentOptions,
    selectedPaymentOption,
    setSelectedPaymentOption,
    currencyCode,
    loading,
    disabled = false,
    ignoreStatus = false
  } = props

  const isMobile = useMatchMedia({ breakpoint: COLLAPSE_BREAKPOINT_PX, prefix: 'max' })
  const [expanded, setExpanded] = useState(false)

  if (loading) return <CoreLoader />
  if (!isValidArray(paymentOptions)) return null

  const isSelected = (method: PaymentMethodOption) => selectedPaymentOption?.paymentMethod === method.paymentMethod
  const selectMethod = (method: PaymentMethodOption) => setSelectedPaymentOption?.(method.paymentMethod, method.path)

  // Desktop: static grid; selecting is the only action (details show in the side panel).
  if (!isMobile) {
    return (
      <div className={styles['deposit-payment-methods']}>
        {paymentOptions.map(method => (
          <MethodCard
            key={method.paymentMethod}
            method={method}
            selected={isSelected(method)}
            disabled={isMethodDisabled(method, { disabled, ignoreStatus })}
            onClick={() => !isSelected(method) && selectMethod(method)}
          />
        ))}
      </div>
    )
  }

  // Mobile: the selected method stays pinned; collapsed reveals its details card, expanding
  // hides the details and animates the remaining methods open so another can be picked.
  const selectedOption = selectedPaymentOption
    ? paymentOptions.find(method => method.paymentMethod === selectedPaymentOption.paymentMethod)
    : undefined
  const listMethods = selectedOption ? paymentOptions.filter(method => !isSelected(method)) : paymentOptions

  const handleListClick = (method: PaymentMethodOption) => {
    selectMethod(method)
    setExpanded(false)
  }

  return (
    <div className={styles['deposit-methods-collapsible']}>
      {selectedOption && (
        <MethodCard
          method={selectedOption}
          selected
          disabled={false}
          collapsible
          expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        />
      )}

      <Collapsible open={Boolean(selectedOption) && !expanded}>
        {selectedOption && (
          <div className={styles['deposit-collapsible-content']}>
            <div className={styles['deposit-method-accordion']}>
              <DepositMethodInfoCard method={selectedOption} currencyCode={currencyCode} variant='inline' />
            </div>
          </div>
        )}
      </Collapsible>

      <Collapsible open={!selectedOption || expanded}>
        <div className={cn(styles['deposit-collapsible-content'], styles['deposit-method-list'])}>
          {listMethods.map(method => (
            <MethodCard
              key={method.paymentMethod}
              method={method}
              selected={isSelected(method)}
              disabled={isMethodDisabled(method, { disabled, ignoreStatus })}
              onClick={() => handleListClick(method)}
            />
          ))}
        </div>
      </Collapsible>
    </div>
  )
}
